import { useEffect, useState, type FunctionComponent } from "react";
import { Alert, Form, InputNumber, message, Modal, Typography } from "antd";
import { useTranslation } from "react-i18next";

import { productActions } from "@fitness-app/app-store";
import {
  EndAccessStrategy,
  PaymentTypeOption,
  ProductPriceAction,
  type OneTimePrice,
  type ProductPrice,
  type RecurringPrice,
  type UpdateProductPriceDTO,
} from "@fitness-app/data-models/entities/Product";
import { getInstallmentCadenceProblem } from "@fitness-app/utils/src/products/installmentCadence";

import { useAppDispatch } from "~/store/initializeStore";

interface DuplicatePriceModalProps {
  price: ProductPrice;
  productId: string;
  open: boolean;
  onClose: () => void;
}

interface DuplicatePriceFormModel {
  amount: number;
  installments?: number | null;
}

/**
 * The cadence a duplicate has to reproduce. `accessPeriod` is what the rest of the admin
 * reads a price by; `stripePrice.recurring` is the fallback for older rows written before
 * the access period was mirrored onto our record.
 */
const getCadence = (price: ProductPrice) => ({
  interval: price.accessPeriod?.interval ?? price.stripePrice.recurring?.interval ?? "month",
  interval_count: price.accessPeriod?.interval_count ?? price.stripePrice.recurring?.interval_count ?? 1,
});

const buildDuplicateDto = (
  price: ProductPrice,
  productId: string,
  { amount, installments }: DuplicatePriceFormModel,
): UpdateProductPriceDTO => {
  const cadence = getCadence(price);
  const base = {
    amount,
    currency: price.currency,
    active: true,
    paymentClient: price.paymentClient,
    ...(installments ? { installments } : {}),
  };

  if (price.type === PaymentTypeOption.Recurring) {
    return {
      action: ProductPriceAction.Create,
      productId,
      copyConfigFromPriceId: price.id,
      price: {
        ...base,
        ...cadence,
        type: PaymentTypeOption.Recurring,
        ...(price.customDuration ? { customDuration: price.customDuration } : {}),
      } as RecurringPrice,
    };
  }

  return {
    action: ProductPriceAction.Create,
    productId,
    copyConfigFromPriceId: price.id,
    price: {
      ...base,
      type: PaymentTypeOption.OneTime,
      endAccessStrategy:
        price.accessPeriod?.type === PaymentTypeOption.OneTime
          ? (price.accessPeriod.endAccessStrategy ?? EndAccessStrategy.Cancel)
          : EndAccessStrategy.Cancel,
      // A one-time price only carries a cadence when it grants access for a period.
      ...(price.accessPeriod ? cadence : {}),
    } as OneTimePrice,
  };
};

export const DuplicatePriceModal: FunctionComponent<DuplicatePriceModalProps> = ({
  price,
  productId,
  open,
  onClose,
}) => {
  const { t } = useTranslation(["products", "common"]);
  const [formController] = Form.useForm<DuplicatePriceFormModel>();
  const [saving, toggleSaving] = useState(false);
  const dispatch = useAppDispatch();
  const cadence = getCadence(price);

  useEffect(() => {
    if (open) {
      formController.setFieldsValue({
        amount: (price.unit_amount ?? 0) / 100,
        installments: price.installments ?? null,
      });
    }
  }, [open, price.id, price.unit_amount, price.installments, formController]);

  const onSubmit = async (model: DuplicatePriceFormModel) => {
    toggleSaving(true);

    await dispatch(
      productActions.updateProductPrices({
        payload: { prices: [buildDuplicateDto(price, productId, model)] },
        onSuccess: () => {
          toggleSaving(false);
          void message.success(t("prices.duplicateSuccess"));
          onClose();
        },
        onFailure: () => {
          toggleSaving(false);
          void message.error(t("errorWhenUpdatingPrice"));
        },
      }),
    );
  };

  return (
    <Modal
      open={open}
      width={520}
      onCancel={onClose}
      okText={t("prices.duplicateSubmit")}
      title={t("prices.duplicateTitle")}
      onOk={() => formController.submit()}
      okButtonProps={{ loading: saving, disabled: saving }}
    >
      <Alert className="mb-4" type="info" showIcon message={t("prices.duplicateInfo")} />

      <Form<DuplicatePriceFormModel> form={formController} onFinish={onSubmit} labelCol={{ span: 8 }}>
        <Form.Item
          label={t("prices.duplicateAmount")}
          name="amount"
          rules={[
            { required: true, type: "number", min: 0, message: t<string>("common:validationErrors.fieldIsRequired") },
          ]}
        >
          <InputNumber
            min={0}
            max={100000}
            precision={2}
            addonAfter={price.currency.toUpperCase()}
            decimalSeparator={price.currency === "pln" ? "," : "."}
          />
        </Form.Item>

        <Form.Item
          label={t("prices.duplicateInstallments")}
          name="installments"
          tooltip={t("prices.duplicateInstallmentsTooltip")}
          rules={[
            {
              validator: (_, value: number | null) => {
                const problem = getInstallmentCadenceProblem({ installments: value, ...cadence });

                return problem
                  ? Promise.reject(
                      new Error(
                        t<string>("prices.duplicateInstallmentsCadenceError", {
                          interval: problem.interval,
                          intervalCount: problem.intervalCount,
                        }),
                      ),
                    )
                  : Promise.resolve();
              },
            },
          ]}
        >
          <InputNumber min={1} max={60} precision={0} />
        </Form.Item>
      </Form>

      <Typography.Text type="secondary">
        {t("prices.duplicateCadence", { count: cadence.interval_count, interval: cadence.interval })}
      </Typography.Text>
    </Modal>
  );
};

export default DuplicatePriceModal;
